// The lists a badge offers: what this machine could forge, what it could build,
// and what the piece under it could climb to — each row priced and, where it is
// off, saying why.

import { type Color, type PieceType, pieceType } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { pieceAt } from "src/engine/rules/position";
import { tallySide } from "src/engine/rules/tally";
import { canForge } from "src/engine/rules/industry/machines";
import {
  canTakeVariant,
  shortRow,
  type UpgradeError,
  upgradeBill,
  upgradeSources
} from "src/engine/rules/industry/upgrades";
import { mintFacingAt, targetAt } from "src/engine/rules/industry/state";
import { isTurretKind } from "src/engine/rules/industry/turret";
import { hasPower } from "src/engine/rules/campaign/powers";
import { FUSE_ERROR_REFUSAL, fuseChoices } from "src/engine/rules/campaign/fusion";
import { BUILD_SAYS, UPGRADE_REFUSAL as UPGRADE_SAYS } from "../refusals";
import {
  type BuildError,
  BUILD_KINDS,
  buildFunds,
  canBuild,
  structureBill,
  STRUCTURE_NAMES
} from "src/engine/rules/industry/structures";
import {
  type Grade, MINTABLE,
  variantsFor, pieceVariant, RULES } from "src/engine/rules/rules";
import { type Bill, materialName } from "src/engine/rules/materials";
import type { BadgeCore, BuildOption, ForgeOption, FuseOption, UpgradeOption } from "./types";

/**
 * The forge list: every piece this foundry could make, with the ones it cannot
 * make right now switched off.
 *
 * **It no longer says why**, because nothing reads the answer any more — see
 * `ForgeOption`. What survives is the count: `⚒ N` on the badge is how many of
 * these are on, and `selection.ts` asks whether the list is non-empty to know
 * it is looking at a foundry.
 */
export function forgeOptions(state: GameState, sq: number, owner: Color): ForgeOption[] {
  const yours = state.position.turn === owner;

  return MINTABLE.map((type: PieceType) => {
    // Asked of the rules rather than re-derived here. This used to test room
    // and price itself, which agreed with `canForge` on those two and knew
    // nothing about the third: a king that had already dug this turn was shown
    // an enabled forge button that the reducer then refused. Turn is the one
    // condition the rules cannot answer, because `canForge` is also asked
    // about the opponent's machines when drawing their badges.
    const why = canForge(state.position, state.industry, sq, owner, type);
    return { type, enabled: yours && why === null };
  });
}

/**
 * The build list, priced and refused exactly like a recipe.
 *
 * Beside the recipes rather than somewhere else, because it is the same
 * decision from the same machine on the same turn: this foundry's one action
 * buys a piece or a building.
 *
 * **The turret was built and unreachable.** Every command behind it shipped —
 * `build` accepts one, `fireTurrets` fires it at its owner's End Turn
 * (§31 S8), and the keystone artifact grants the power
 * off a king kill — and this list was `[STORE]`, hardcoded. A player could
 * earn Emplacement and the game was identical. That is the failure this
 * codebase keeps paying for: rules, a cost and a power with nothing joining
 * them, and it slipped past §22.0's greps because every *command* existed.
 *
 * **Listed even without the power, disabled and saying so.** A row nobody can
 * click yet is how a player learns there is something to go and find; a
 * mechanic that appears from nowhere the turn an artifact lands is one they
 * meet by accident. A refusal because a keystone can still turn up —
 * `upgradeOptions` omits its rung instead, since no prize unlocks one.
 */
export function buildOptions(state: GameState, sq: number, owner: Color): BuildOption[] {
  const yours = state.position.turn === owner;
  // The rules are the authority — `build` refuses a turret without this and
  // says so (§14.10 hazard 24: gate it in the reducer, not only in the UI, or
  // the socket is the gate). This only decides whether to draw a live button.
  const emplaced = hasPower(state.industry, owner, "emplacement");
  return BUILD_KINDS.map((kind) => {
    const bill = structureBill(kind);
    // The row actually short is the one the refusal quotes. The line as well
    // as the rung, matching what `canBuild` counts — a hopper of steel is not one of stone.
    // `buildFunds`: the piece's inbox, then the side's stores (§31 S3).
    const shortRow =
      bill.find((row) => buildFunds(state.industry, sq, owner, row, sq) < row.count) ?? bill[0];
    const cost = shortRow.count;
    const grade = shortRow.grade as Grade;
    const have = buildFunds(state.industry, sq, owner, shortRow, sq);
    const why = canBuild(state.position, state.industry, sq, owner, kind);
    const ungranted = isTurretKind(kind) && !emplaced;
    const reason = !yours
      ? "Not your turn."
      : ungranted
      ? "Turrets need a Targeting Array."
      : why
      ? BUILD_REFUSAL(why, cost, have, materialName(shortRow.line, grade))
      : undefined;
    return {
      kind,
      name: STRUCTURE_NAMES[kind],
      cost,
      grade,
      have,
      enabled: !reason,
      reason
    };
  });
}

/**
 * Every variant row this piece could take, priced, with the ones it cannot
 * take switched off and *saying why*. The rank ladder went with the Clone Army
 * (§31 R2). The reducer is still the authority; this only decides what to draw.
 */
export function upgradeOptions(state: GameState, sq: number, owner: Color): UpgradeOption[] {
  const piece = pieceAt(state.position, sq);
  if (!piece) return [];
  const from = pieceType(piece);
  const yours = state.position.turn === owner;

  // A row the player's army may not build is omitted, not disabled.
  return variantsFor(from)
        .filter((id) => !pieceVariant(id).playerOnly || owner === tallySide(state.run))
        .map((id) => {
          const bill = upgradeBill(state.industry, owner, id);
          // The row the likeliest source is short of, priced against it: the
          // first source that covers the bill, else the first in reach.
          const sources = upgradeSources(state.position, state.industry, sq, owner);
          const paying =
            sources.find((one) => !shortRow(state.industry, one, owner, bill)) ?? sources[0];
          const short =
            paying === undefined ? null : shortRow(state.industry, paying, owner, bill);
          const row = short?.row ?? bill[0];
          const have = paying === undefined ? 0 : (short?.have ?? row.count);
          const why = canTakeVariant(state.position, state.industry, sq, owner, id);
          const price = priceOf(bill);
          // **"Not for sale" outranks "not your turn"**, unlike every other
          // refusal here: whose turn it is has nothing to do with a row no
          // amount of metal buys, and saying "wait your turn" about one sends
          // the player back to try again.
          const reason =
            why === "not-for-sale"
              ? UPGRADE_REFUSAL(why, price, pieceVariant(id).grantedBy)
              : !yours
                ? "Not your turn."
                : why
                  ? UPGRADE_REFUSAL(why, price)
                  : undefined;
          return {
            // The rank it stays, which is what the reducer will leave on the
            // board — the row is carried in `variant` beside it.
            target: from,
            variant: id,
            name: pieceVariant(id).name,
            cost: row.count,
            grade: row.grade,
            line: row.line,
            have,
            price,
            enabled: !reason,
            reason
          };
        });
}

/**
 * The fuses this piece could join (§31 P4), with its partners standing by —
 * priced, and saying why when off. `fuseChoices` is the rule; this is words.
 */
export function fuseOptions(state: GameState, sq: number, owner: Color): FuseOption[] {
  const yours = state.position.turn === owner;
  return fuseChoices(state, owner, sq).map(({ into, parts, why }) => {
    const row = pieceVariant(into);
    const price = priceOf(upgradeBill(state.industry, owner, into));
    const reason = !yours
      ? "Not your turn."
      : why === "too-poor"
        ? `Needs ${price} from a forger beside them or an Armory in reach.`
        : why
          ? sentence(FUSE_ERROR_REFUSAL[why])
          : undefined;
    return { target: row.base, variant: into, name: row.name, parts, price, enabled: !reason, reason };
  });
}

/** "a refusal" → "A refusal." */
const sentence = (text: string): string => `${text[0].toUpperCase()}${text.slice(1)}.`;

/** A bill and its fuel in words: "5 crucible steel + 1 electrum + 2 fuel". */
export function priceOf(bill: Bill): string {
  return [
    ...bill.map((row) => `${row.count} ${materialName(row.line, row.grade)}`),
    `${RULES.upgradeFuel} fuel`
  ].join(" + ");
}

function UPGRADE_REFUSAL(
  why: UpgradeError,
  /** The whole price in words, which is what a poor or unfuelled row says. */
  price: string,
  /** What grants the row, for the rows no metal buys. See `PieceVariant`. */
  grantedBy?: string
): string {
  switch (why) {
    case "not-for-sale":
      // Names the card, because this is the turret's shape: a row switched off
      // is how a player learns the mechanic exists before the card lands.
      return `Not for sale — ${grantedBy ?? "a card"} grants this one.`;
    case "too-poor":
      // One source pays it all (§31 P3): a forger beside it, or an Armory
      // in reach out of your nearest stores.
      return `Needs ${price} from a forger beside it or an Armory in reach.`;
    case "no-fuel":
      return `Needs ${RULES.upgradeFuel} fuel at the source — power in reach, its pool, or coal it holds.`;
    case "cannot-climb":
      return "This rank is the top of the ladder.";
    case "not-that-rank":
      return "Not the next rung.";
    case "already-a-variant":
      // Says what it *is*, because "already a variant" reads as jargon and the
      // player's question is why the other button went dead.
      return "This piece has already been specialised.";
    case "not-your-piece":
      return "Not your piece.";
    case "no-armory":
    case "out-of-armory-reach":
      return UPGRADE_SAYS[why];
    default:
      return "Cannot climb.";
  }
}

function BUILD_REFUSAL(
  why: BuildError,
  cost: number,
  have: number,
  /** What the rung is *called* — a building is stone, not `GRADE_NAMES`' metal. */
  material: string
): string {
  switch (why) {
    case "too-poor":
      return `${cost - have} more of ${material} or better.`;
    case "no-room":
      return "No bare ground beside it.";
    case "not-your-piece":
      return "Not your piece.";
    case "not-beside-you":
      return "Build beside one of your pieces.";
    case "square-not-empty":
      return "That square is not bare.";
    default:
      return BUILD_SAYS[why];
  }
}

/**
 * The forge half of a badge, for a piece that is wearing a different hat.
 *
 * A king standing on a heap is a miner *this turn*, and it used to get the
 * miner badge and stop there — so the one place that says what it could build
 * and what that costs was unreachable for as long as it was digging. Forging
 * is a choice it makes *instead* of mining, which makes the recipe list the
 * whole decision; hiding it until the king steps off the scrap hides the
 * option at exactly the moment it is worth taking.
 */
export function forgeFields(state: GameState, sq: number, owner: Color): Partial<BadgeCore> {
  const recipes = forgeOptions(state, sq, owner);
  return {
    recipes,
    builds: buildOptions(state, sq, owner),
    target: targetAt(state.industry, sq) as PieceType | 0,
    mintFacing: mintFacingAt(state.industry, sq),
    // Only when something is actually affordable: a prompt on a king that
    // cannot pay for anything is a badge that always glows.
    needsSetup: recipes.some((option) => option.enabled)
  };
}
